import React, { useEffect, useMemo, useState, forwardRef, useRef } from 'react';
import PropTypes from 'prop-types';
import { Button, ButtonGroup, Dropdown, DropdownButton } from 'react-bootstrap';
import Flex from 'components/common/Flex';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import AdvanceTableWrapper from 'components/common/tables/AdvanceTableWrapper';
import AdvanceTable from 'components/common/tables/AdvanceTable';
import AdvanceTablePagination from 'components/common/tables/AdvanceTablePagination';
import AdvanceTableSearchBox from 'components/common/tables/AdvanceTableSearchBox';
import IconButton from 'components/common/IconButton';
import Inputs from 'components/common/Input';

const IndeterminateCheckbox = forwardRef(({ indeterminate, ...rest }, ref) => {
 const defaultRef = useRef();
 const resolvedRef = ref || defaultRef;

 useEffect(() => {
  resolvedRef.current.indeterminate = indeterminate;
 }, [resolvedRef, indeterminate]);

 return <input type="checkbox" id="All" ref={resolvedRef} {...rest} className="form-check-input m-1" />;
});

const BulAction = ({
 selectedRowIds,
 globalFilter,
 setGlobalFilter,
 //groupBy,
 //expanded,
 allColumns,
 getToggleHideAllColumnsProps,
 resetData,
 updateMyData
}) => {
 //console.log({ selectedRowIds, allColumns }, Object.keys(selectedRowIds));
 const [select, setSelect] = useState(null);
 const updateData = () => {
  if (select === 'delete') updateMyData(null, null, Object.keys(selectedRowIds), 3);
  else if (select !== 'Acciones masivas') updateMyData(Object.keys(selectedRowIds), 'status', select);
  setSelect(null);
 };
 return (
  <ul className="mb-0 list-unstyled">
   <li className="alert mb-0 rounded-bottom-0 rounded-top-3 py-2 px-card alert-primary border-x-0 border-top-0">
    <Flex className="fs--2 px-2" alignItems="center" justifyContent="end" wrap="wrap">
     <span className="fa-stack me-2 ms-n1 circle-dashed" style={{ width: '35px', height: '35px' }}>
      <FontAwesomeIcon icon="circle" className="fa-stack-2x fs--1 text-300" />
      <FontAwesomeIcon icon="network-wired" className="fa-inverse fa-stack-1x fs-0 text-primary" ransform="shrink-3" />
     </span>
     <div className="flex-1">
      <h6 className="mb-0 text-primary">
       <span className="pe-3">Rutas y Permisos</span>
      </h6>
      <p className="mb-0">Establece las rutas y permisos para el usuario</p>
     </div>
     {Object.keys(selectedRowIds).length > 0 ? (
      <div className="d-flex">
       <Inputs
        type="select"
        name="bulk-actions"
        placeholder="Acciones masivas"
        errors={{}}
        options={[
         { label: 'Activar', value: 'available' },
         { label: 'Inactivar', value: 'inactive' },
         { label: 'Eliminar', value: 'delete' }
        ]}
        formControlProps={{ size: 'sm', 'aria-label': 'Bulk actions', value: select, onChange: e => setSelect(e.target.value) }}
       />
       <Button type="button" variant="falcon-default" size="sm" className="ms-2" onClick={updateData}>
        Apply
       </Button>
      </div>
     ) : (
      <Flex wrap="wrap-reverse" justifyContent="end">
       <div className="p-1 flex-grow-1">
        <AdvanceTableSearchBox globalFilter={globalFilter} setGlobalFilter={setGlobalFilter} placeholder="buscar..." />
       </div>

       <div className="p-1 d-none d-sm-inline-block">
        <DropdownButton
         as={ButtonGroup}
         size="sm"
         id={'dropdown-variants'}
         variant="falcon-default"
         title={
          <>
           <FontAwesomeIcon icon={faEyeSlash} className="me-1" transform="shrink-3" />
           <span className="d-none d-sm-inline-block">Ocultar</span>
          </>
         }
        >
         <Dropdown.ItemText>Oculta columnas de la tabla</Dropdown.ItemText>
         <div className="dropdown-item cursor-pointer">
          <div className="cursor-pointer mb-0 form-check p-0">
           <IndeterminateCheckbox {...getToggleHideAllColumnsProps()} />
           <label title="Toggle Column Visible" htmlFor="All" className="form-check-label m-0">
            Todos
           </label>
          </div>
         </div>

         <Dropdown.Divider />
         {allColumns.map(column => (
          <div key={column.id} className="dropdown-item cursor-pointer">
           <div className="cursor-pointer mb-0 form-check p-0">
            <input type="checkbox" id={column.id} className="form-check-input m-1" {...column.getToggleHiddenProps()} />
            <label title="Toggle Column Visible" htmlFor={column.id} className="form-check-label m-0">
             {column.id}
            </label>
           </div>
          </div>
         ))}
        </DropdownButton>
       </div>

       <div className="p-1">
        <IconButton variant="falcon-default" size="sm" icon="plus" transform="shrink-3" onClick={() => resetData()}>
         <span className="d-none d-sm-inline-block">Crear ruta</span>
        </IconButton>
       </div>
      </Flex>
     )}
    </Flex>
   </li>
  </ul>
 );
};

/* function roundedMedian(leafValues) {
 let min = leafValues[0] || 0;
 let max = leafValues[0] || 0;

 leafValues.forEach(value => {
  min = Math.min(min, value);
  max = Math.max(max, value);
 });

 return Math.round((min + max) / 2);
} */

const TableRoutes = ({ data, setData, product, setProduct }) => {
 const [skipPageReset, setSkipPageReset] = useState(false);
 const [editableRowIndex, setEditableRowIndex] = useState({
  currentIndex: null,
  data: null
 });

 const columns = useMemo(
  () => [
   {
    accessor: 'id',
    Header: 'Id',
    maxWidth: 50,
    aggregate: 'count',
    Aggregated: ({ value }) => `${value} IDS`
   },
   {
    accessor: 'category',
    Header: 'Categorias',
    aggregate: 'uniqueCount',
    Aggregated: ({ value }) => `${value} Unique Names`
   },
   {
    accessor: 'creator',
    Header: 'Creador',
    cellProps: {
     className: 'fw-medium'
    },
    aggregate: 'average',
    Aggregated: ({ value }) => `${Math.round(value * 100) / 100} (avg)`
   }
  ],
  []
 );

 // Necesitamos evitar que la tabla reinicie el índice de página cuando
 // Actualizar datos. Entonces podemos hacer un seguimiento de esa bandera con una referencia.

 // Cuando nuestro renderizador de celda llame a updateMyData, usaremos
 // el índice de fila, el ID de columna y el nuevo valor para actualizar el
 // datos originales

 const updateMyData = (index, column, value, type = 0) => {
  // También activamos la bandera para no resetear la página
  setSkipPageReset(true);
  const indices = Array.isArray(index) ? index : [];

  if (type === 1)
   setData(old => ({ ...old, products: old.products.map((row, i) => (i === index ? { ...old.products[i], ...value } : row)) }));
  else if (type === 2) setData(old => ({ ...old, products: old.products.concat([value]) }));
  else if (type === 3) setData(old => ({ ...old, products: old.products.filter((_, i) => !value.some(e => e == i)) }));
  else
   setData(old => ({
    ...old,
    products: old.products.map((row, i) =>
     i === index || indices.some(e => e == i) ? { ...old.products[i], [column]: value } : row
    )
   }));
 };

 const getRowProps = row => row.index === editableRowIndex.currentIndex && { className: 'table-primary' };

 useEffect(() => {
  if (editableRowIndex?.currentIndex > -1) {
   setSkipPageReset(true);
   setProduct(editableRowIndex);
  }
 }, [editableRowIndex.currentIndex]);

 useEffect(() => {
  if (!product) setEditableRowIndex({ currentIndex: null, data: null });
 }, [product]);

 const resetData = () => {
  console.log('no hay nada que resetear');
 };

 return (
  <AdvanceTableWrapper
   columns={columns}
   data={data}
   sortable
   pagination
   perPage={5}
   selection
   selectionColumnWidth={30}
   updateMyData={updateMyData}
   skipPageReset={skipPageReset}
   getRowProps={getRowProps}
   editableRowIndex={editableRowIndex}
   setEditableRowIndex={setEditableRowIndex}
   resetData={resetData}
  >
   <BulAction table />
   <AdvanceTable
    table
    headerClassName="bg-200 text-900 text-nowrap align-middle"
    rowClassName="align-middle white-space-nowrap"
    tableProps={{
     striped: true,
     className: 'fs--1 mb-0 overflow-hidden',
     responsive: true,
     hover: true,
     size: 'sm'
    }}
   />
   <div className="mt-3">
    <AdvanceTablePagination table />
   </div>
  </AdvanceTableWrapper>
 );
};

IndeterminateCheckbox.propTypes = { indeterminate: PropTypes.any };

BulAction.propTypes = {
 selectedRowIds: PropTypes.object,
 globalFilter: PropTypes.any,
 setGlobalFilter: PropTypes.any,
 groupBy: PropTypes.any,
 expanded: PropTypes.any,
 allColumns: PropTypes.any,
 getToggleHideAllColumnsProps: PropTypes.any,
 resetData: PropTypes.any,
 updateMyData: PropTypes.func
};

TableRoutes.propTypes = {
 data: PropTypes.array,
 setData: PropTypes.func,
 product: PropTypes.object,
 setProduct: PropTypes.func
};

export default TableRoutes;
